import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAbout } from "@/hooks/use-about";
import { Link, useNavigate } from "react-router-dom";
import {
  Users,
  Heart,
  Star,
  ArrowRight,
  Target,
  Medal,
  Globe,
  Briefcase,
} from "lucide-react";

const SectionTitle = ({ title }: { title: string }) => (
  <h2
    className="text-3xl md:text-4xl font-bold text-[#1A2B4D] mb-8 animate-fade-in opacity-0"
    style={{ animationDelay: "0.2s" }}
  >
    {title}
  </h2>
);

const About = () => {
  const {
    heroContent,
    storyContent,
    visionContent,
    missionContent,
    coreValues,
  } = useAbout();
  const navigate = useNavigate();

  const handleSubscribeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Chuyển hướng đến trang chủ và gắn cờ để biết cần scroll xuống phần nào
    navigate("/?scrollTo=join-community");
  };

  return (
    <>
      <Navbar />

      <main className="pt-20">
        {/* Hero section */}
        <section className="relative py-16 md:py-24 bg-[#1A2B4D] overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-gold/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-gold/10 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl"></div>

          <div className="container mx-auto px-4 md:px-6 relative">
            <div className="max-w-3xl mx-auto text-center">
              <span
                className="inline-block px-3 py-1 bg-white/10 text-white text-sm rounded-full mb-4 animate-slide-in-up opacity-0"
                style={{ animationDelay: "0.1s" }}
              >
                About Us
              </span>
              <h1
                className="text-4xl md:text-5xl font-bold text-white mb-6 animate-slide-in-up opacity-0"
                style={{ animationDelay: "0.2s" }}
              >
                {heroContent.title}
              </h1>
              <p
                className="text-xl text-white/90 leading-relaxed animate-slide-in-up opacity-0"
                style={{ animationDelay: "0.3s" }}
              >
                {heroContent.subtitle}
              </p>
            </div>
          </div>
        </section>

        {/* The Story of GlowHer.Life */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div
                className="animate-fade-in-left opacity-0"
                style={{ animationDelay: "0.2s" }}
              >
                <SectionTitle title={storyContent.title} />
                <div className="prose prose-lg max-w-none text-[#1A2B4D]/80">
                  {storyContent.paragraphs.map((paragraph, index) => (
                    <p
                      key={index}
                      className="mb-4 animate-fade-in opacity-0"
                      style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                    >
                      {paragraph.startsWith("💬") ? (
                        <span className="bg-[#1A2B4D]/5 px-3 py-1 rounded text-[#1A2B4D]">
                          {paragraph}
                        </span>
                      ) : paragraph.startsWith("🌟") ? (
                        <span className="text-xl font-medium text-[#1A2B4D]">
                          {paragraph}
                        </span>
                      ) : (
                        paragraph
                      )}
                    </p>
                  ))}
                  <p
                    className="animate-fade-in opacity-0"
                    style={{ animationDelay: "0.7s" }}
                  >
                    💡 The Core Message of GlowHer.Life
                  </p>
                  {storyContent.coreMessages.map((message, index) => (
                    <p
                      key={index}
                      className={
                        index !== 0
                          ? "animate-fade-in opacity-0"
                          : "animate-fade-in opacity-0"
                      }
                      style={{ animationDelay: `${0.8 + index * 0.1}s` }}
                    >
                      {message}
                    </p>
                  ))}
                </div>
              </div>
              <div
                className="animate-fade-in-right opacity-0"
                style={{ animationDelay: "0.4s" }}
              >
                <img
                  src={storyContent.image}
                  alt="A contemplative woman with a cup of tea"
                  className="rounded-lg shadow-xl w-full h-auto object-cover hover-lift transition-all duration-500"
                />
              </div>
            </div>

            <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div
                className="order-2 md:order-1 animate-fade-in-left opacity-0"
                style={{ animationDelay: "0.4s" }}
              >
                <img
                  src={visionContent.image}
                  alt="A diverse group of women connecting"
                  className="rounded-lg shadow-xl w-full h-auto object-cover hover-lift transition-all duration-500"
                />
              </div>
              <div
                className="order-1 md:order-2 animate-fade-in-right opacity-0"
                style={{ animationDelay: "0.2s" }}
              >
                <SectionTitle title={visionContent.title} />
                <div className="prose prose-lg max-w-none text-[#1A2B4D]/80">
                  <ul className="space-y-4 mb-6">
                    {visionContent.points.map((point, index) => (
                      <li
                        key={index}
                        className="flex items-start animate-fade-in opacity-0"
                        style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                      >
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                  <p
                    className="mb-4 animate-fade-in opacity-0"
                    style={{ animationDelay: "0.6s" }}
                  >
                    <span className="text-[#1A2B4D]">
                      {visionContent.additionalText}
                    </span>
                  </p>
                  <p
                    className="animate-fade-in opacity-0"
                    style={{ animationDelay: "0.7s" }}
                  >
                    🌍 Our Long-Term Goals
                  </p>
                  {visionContent.goals.map((goal, index) => (
                    <p
                      key={index}
                      className="animate-fade-in opacity-0"
                      style={{ animationDelay: `${0.8 + index * 0.1}s` }}
                    >
                      {goal}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Mission Section */}
        <section className="py-6 md:py-6 bg-gradient-to-br from-[#1A2B4D]/5 to-transparent">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2
                  className="text-3xl md:text-4xl font-bold text-[#1A2B4D] mb-6 animate-fade-in opacity-0"
                  style={{ animationDelay: "0.2s" }}
                >
                  {missionContent.title}
                </h2>
                <div className="prose prose-lg max-w-none text-[#1A2B4D]/80 space-y-6">
                  <p
                    className="animate-fade-in opacity-0"
                    style={{ animationDelay: "0.3s" }}
                  >
                    {missionContent.text}
                  </p>
                  <div
                    className="bg-white/50 p-6 rounded-lg border border-gold/20 shadow-sm animate-fade-in opacity-0 hover-lift transition-all duration-300"
                    style={{ animationDelay: "0.4s" }}
                  >
                    <h3 className="text-2xl font-medium text-[#1A2B4D] text-center mb-4">
                      {missionContent.slogan}
                    </h3>
                    <p className="text-[#1A2B4D]/80">
                      {missionContent.sloganText}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Core Values Section */}
        <section className="py-6 md:py-12">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h2
                className="text-3xl md:text-4xl font-bold text-[#1A2B4D] mb-12 animate-fade-in opacity-0"
                style={{ animationDelay: "0.2s" }}
              >
                Our Core Values
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {coreValues.map((value, index) => (
                <div
                  key={index}
                  className="bg-white p-8 rounded-xl shadow-sm border border-gold/20 hover:shadow-lg transition-all duration-300 hover:-translate-y-2 animate-fade-in opacity-0"
                  style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                >
                  <div className="text-2xl mb-4">✨</div>
                  <h3 className="text-xl font-bold text-[#1A2B4D] mb-4">
                    {value.title}
                  </h3>
                  <div className="space-y-4 text-[#1A2B4D]/80">
                    {value.description.map((desc, i) => (
                      <p key={i}>{desc}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Join Us CTA */}
        <section className="py-16 md:py-24 bg-[#1A2B4D]">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-xl mx-auto text-center">
              <h2
                className="text-3xl md:text-4xl font-bold text-white mb-6 animate-fade-in opacity-0"
                style={{ animationDelay: "0.2s" }}
              >
                Join the GlowHer.Life Community
              </h2>
              <p
                className="text-xl text-white/90 mb-8 animate-fade-in opacity-0"
                style={{ animationDelay: "0.3s" }}
              >
                Connect with thousands of like-minded women who are embracing
                smart living and timeless elegance.
              </p>
              <div
                className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in opacity-0"
                style={{ animationDelay: "0.4s" }}
              >
                <a
                  href="/#join-community"
                  className="bg-white hover:bg-white/90 text-[#1A2B4D] font-medium px-6 py-3 rounded-lg transition-all duration-300 flex items-center justify-center hover:scale-105"
                  onClick={handleSubscribeClick}
                >
                  Subscribe to Newsletter
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
                <a
                  href="/contact"
                  className="bg-transparent hover:bg-white/10 text-white border border-white/30 font-medium px-6 py-3 rounded-lg transition-all duration-300 hover:scale-105"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default About;
